import * as Alert from 'components/alert';
import * as Form from 'components/form';
import { useForm } from 'components/form';
import * as Page from 'components/page';
import { ContextAuth as CA } from 'contexts';
import { cnpj } from 'cpf-cnpj-validator';
import { useApi, useApiLocation, useConversionOptions } from 'hooks';
import { RHP } from 'providers';
import React, { useEffect, useState } from 'react';
import { Prompt } from 'react-router';
import { PocsCard } from '../components/pocs/PocsCard';
import * as Styled from './styles';

export function CorporateStep2({ useRankingCorporate }) {
    const { vars, updateSteps, putCompanyDetails, t } = useRankingCorporate;
    const { ranking, companyDetails, companyPocs } = vars;

    const apiLocation = useApiLocation();
    const { state } = React.useContext(CA);

    const [countries, setCountries] = useState([]);
    const [groupName, setGroupName] = useState('');
    const [authorizedToEdit, setAuthorizedToEdit] = useState(false);

    const isCompanyValid = companyDetails.valid;
    const corpHasSector = companyDetails.companySector;

    const isOperational = state?.tokn?.pocs.includes('operational');
    const corpHasPocs = companyDetails.hasPocs > 0;

    const api = useApi();

    const [formDataChanged, setFormDataChanged] = useState(false);

    // Initialize a new form handler
    const form = useForm({
        ...vars.application,
        name: companyDetails.name,
        awardName: companyDetails.awardName,
        domains: companyDetails.domains,
        logourl: companyDetails.logourl,
        companySector: companyDetails.companySector,
        revenuesRange: companyDetails.revenuesRange,
        companyType: companyDetails.companyType,
        size: companyDetails.size,
        taxId: companyDetails.taxId,
        country: companyDetails.country,
        multinational: companyDetails.multinational,
        isMiddleMarket: false,
        isPoc: false
    });

    useEffect(() => {
        const initialFormData = {
            ...vars.application,
            name: companyDetails.name,
            awardName: companyDetails.awardName,
            domains: companyDetails.domains,
            logourl: companyDetails.logourl,
            companySector: companyDetails.companySector,
            revenuesRange: companyDetails.revenuesRange,
            companyType: companyDetails.companyType,
            size: companyDetails.size,
            taxId: companyDetails.taxId,
            country: companyDetails.country,
            multinational: companyDetails.multinational,
            isMiddleMarket: false,
            isPoc: false
        };

        let formChanged = false;

        Object.keys(form.values).forEach(key => {
            if (form.values[key] !== initialFormData[key]) {
                formChanged = true;
            }
        });

        setFormDataChanged(formChanged);
    }, [form.values]);

    useEffect(() => {
        const handleBeforeUnload = event => {
            if (formDataChanged) {
                event.preventDefault();

                event.returnValue =
                    'Você tem alterações não salvas. Tem certeza de que deseja sair?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [formDataChanged]);

    useEffect(() => {
        const run = async () => {
            if (companyDetails?.groupid || companyDetails?.groupId) {
                const groupId = companyDetails?.groupid || companyDetails?.groupId;
                const groupCompany = await api.get(`/companies/${groupId}`);

                setGroupName(groupCompany?.name || '');
            }
        };
        run();
    }, []);

    useEffect(() => {
        if (corpHasPocs) {
            if (isOperational) {
                setAuthorizedToEdit(true);
            } else {
                setAuthorizedToEdit(false);
            }
        } else if (!corpHasPocs) {
            setAuthorizedToEdit(false);
        }
    }, [isCompanyValid, authorizedToEdit, corpHasSector, corpHasPocs, isOperational]);

    useEffect(() => {
        apiLocation.geo('').then(res => {
            const names = res.map(country => {
                return { name: country.name, value: country.id };
            });
            setCountries(names);
        });
    }, []);

    const conv = useConversionOptions(['challenge', 'companySize', 'companySector', 'companyType']);
    const conversion = useConversionOptions(['corpInvestmentOrRevenues'], {
        namespace: 'currency'
    });

    if (!companyDetails.rankingData) {
        companyDetails.rankingData = {};
    }

    if (!companyDetails.rankingData?.[ranking.id]) {
        companyDetails.rankingData[ranking.id] = {
            revenuesRange: [],
            investmentRange: [],
            categories: []
        };
    }

    // Check for previous step conclusion
    if (!vars.application.steps || vars.application.steps.step1 !== true) {
        RHP.replace('/ranking/latam/corporate/');
        return null;
    }
    // companyDetails.revenuesRange = [];

    if (!companyDetails.rankingData) {
        companyDetails.rankingData = {};
    }

    if (!companyDetails.rankingData?.[ranking.id]) {
        companyDetails.rankingData[ranking.id] = {
            revenuesRange: [],
            investmentRange: [],
            categories: []
        };
    }

    // let hasRevenuesRange = companyDetails.revenuesRange.length > 0;

    let hasRevenuesRangeDisplayedOption = false;
    conversion.corpInvestmentOrRevenues.forEach(i => {
        if (i.value === `range-${companyDetails.revenuesRange}`) {
            // hasRevenuesRange = true;
            hasRevenuesRangeDisplayedOption = true;
        }
    });

    const onBlur = {
        isValidCNPJ: ev => {
            const { name, value } = ev.target;
            // Clear the errors and warnings before validating
            form.clearMessages({ name });
            // Only check if the value is filled
            if (!value || form.values.country !== 'BR') return false;
            // Check if the CPF is a valid one
            const cleanValue = value.replace(/[^0-9]/g, '');

            if (!cnpj.isValid(cleanValue)) {
                // Emit error message for registered email
                const error = t('StartupStep2.warningTextCnpj');
                if (authorizedToEdit) {
                    return form.emitError({ name, error });
                }
                return false;
            }

            return true;
        }
    };

    const sortedCompanySector = [...conv.companySector].sort((a, b) => {
        const lang = t?.i18n?.language || 'en';
            
        // Ensure "Others" stays last, regardless of language or name
        if (a.value === 'Others') return 1;
        if (b.value === 'Others') return -1;

        return a.name.localeCompare(b.name, lang);
    });
    
    
    const fields = {
        name: {
            error: form.errors.name,
            label: t('ranking:CorporateStep2.companyName'),
            name: 'name',
            type: 'text',
            value: form.values.name,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.name
        },
        awardName: {
            error: form.errors.awardName,
            label: t('ranking:CorporateStep2.awardName'),
            name: 'awardName',
            type: 'text',
            value: form.values.awardName,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.awardName
        },
        logourl: {
            error: form.errors.logourl,
            label: `${t('ranking:CorporateStep2.logoDescr')}`,
            name: 'logourl',
            type: 'CompanyLogo',
            value: form.values.logourl,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.logourl
        },
        domains: {
            error: form.errors.domains,
            label: t('CorporateStep2.associatedDomainsTitle'),
            name: 'domains',
            type: 'text',
            value: form.values.domains,
            readOnly: true
        },
        companySector: {
            error: form.errors.companySector,
            label: `${t('ranking:CorporateStep2.sector')}`,
            name: 'companySector',
            type: 'select',
            options: [{ name: t('glossary:select'), value: '' }, ...sortedCompanySector],
            value: form.values.companySector,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.companySector
        },
        taxId: {
            error: form.errors.taxId,
            label: t('glossary:cnpj'),
            name: 'taxId',
            type: 'generic',
            value: form.values.taxId,
            onBlur: ev => form.handleBlur(ev, onBlur.isValidCNPJ),
            onChange: form.handleChange,
            required: false,
            readOnly: !authorizedToEdit && companyDetails.taxId
        },
        companyType: {
            error: form.errors.companyType,
            label: `${t('ranking:CorporateStep2.companyType')}`,
            name: 'companyType',
            type: 'select',
            options: [{ name: t('glossary:select'), value: '' }, ...conv.companyType],
            value: form.values.companyType,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.companyType
        },
        size: {
            error: form.errors.size,
            label: t('glossary:companySize'),
            name: 'size',
            options: [{ name: t('glossary:select'), value: '' }, ...conv.companySize],
            type: 'select',
            value: form.values.size,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.size
        },
        revenuesRange: {
            error: form.errors.revenuesRange,
            label: `${t('ranking:CorporateStep2.revenuesRange')} (${t('glossary:inDollars')})`,
            name: 'revenuesRange',
            type: 'SelectRangeArray',
            options: [
                !hasRevenuesRangeDisplayedOption
                    ? {
                          name: companyDetails.revenuesRangeText,
                          value: `range-${companyDetails.revenuesRange}`,
                          disabled: !hasRevenuesRangeDisplayedOption
                      }
                    : {
                          name: t('glossary:selectOption'),
                          value: ''
                      },

                ...conversion.corpInvestmentOrRevenues
            ],
            value: form.values.revenuesRange,
            onChange: form.handleChange,
            required: true,
            disabled: !authorizedToEdit
        },
        country: {
            error: form.errors.country,
            label: `${t('ranking:CorporateStep2.companyLocation')}`,
            name: 'country',
            type: 'select',
            // options: countries,
            options: [{ name: t('glossary:select'), value: countries.id }, ...countries],
            value: form.values.country,
            required: true,
            onChange: ev => {
                form.handleChange(ev);
                form.clearMessages({ name: 'taxId' });
            },
            readOnly: !authorizedToEdit && companyDetails.country
        },
        multinational: {
            error: form.errors.multinational,
            label: t('glossary:isMultinational'),
            name: 'multinational',
            type: 'checkbox',
            required: false,
            value: form.values.multinational,
            onChange: form.handleChange,
            disabled: !authorizedToEdit && companyDetails.country
        },
        isMiddleMarket: {
            error: form.errors.isMiddleMarket,
            label: t('glossary:isMiddleMarket'),
            name: 'isMiddleMarket',
            type: 'checkbox',
            required: true,
            value: form.values.isMiddleMarket,
            onChange: form.handleChange,
            disabled: !authorizedToEdit && companyDetails.companyType
        },
        isPoc: {
            error: form.errors.isPoc,
            label: t('CorporateStep2.pocPeople'),
            name: 'isPoc',
            type: 'checkbox',
            required: true,
            value: form.values.isPoc,
            onChange: form.handleChange
        }
    };

    if (form.values.country === 'BR') {
        fields.taxId.displayFormat = '00.000.000/0000-00';
        fields.taxId.maxLength = 18;
        fields.taxId.onChange = e => {
            e.target.value = cnpj.format(e.target.value);
            form.handleChange(e);
        };
    }

    // BANNERS RELATED
    const isCompanySmall =
        fields.size?.value?.toString() === '1' || fields?.size?.value?.toString() === '2';
    const corpDontMeetRequirements =
        isCompanySmall && Number(fields.revenuesRange?.value[1]) <= 20000000;
    const notDeclaredRevenues =
        fields.revenuesRange.value.length === 1 && Number(fields.revenuesRange.value[0]) === 0;

    const isCorp =
        Number(fields.size.value) > 2 || Number(fields.revenuesRange?.value[0]) >= 20000000;
    const isMiddleMarket =
        isCorp &&
        !!fields?.revenuesRange?.value[1] &&
        Number(fields.revenuesRange?.value[1]) <= 200000000;

    const propsCorpDoesntMeetRequirements = {
        descr: t('CorporateStep2.corpDoesntMeetRequirements'),
        icons: 'far fa-exclamation-circle',
        type: 'warning',
        bgColor: '#f44336',
        color: 'white'
    };

    const propsCorpIsMiddleMarket = {
        descr: t('CorporateStep2.corpIsMiddleMarket'),
        icons: 'far fa-info-circle',
        bgColor: 'rgba(69, 170, 242, 0.55)',
        type: 'warning'
    };

    const propsNotDeclaredRevenues = {
        descr: t('CorporateStep2.notDeclaredRevenues'),
        icons: 'far fa-info-circle',
        type: 'warning'
    };

    const propsOutdatedPocs = {
        descr: t('CorporateStep2.confirmPocs'),
        icons: 'far fa-info-circle',
        bgColor: '#FDFD96',
        type: 'warning'
    };

    // Handle form submitions
    const onSubmit = values => {
        setFormDataChanged(false);
        const rankingPayload = {
            ...vars.application,
            revenuesRange: values.revenuesRange
        };
        // Set the current step to true;
        if (!rankingPayload.steps) rankingPayload.steps = {};
        rankingPayload.steps.step2 = true;

        const companyPayload = {
            name: values.name,
            awardName: values.awardName,
            logourl: values.logourl,
            companySector: values.companySector,
            companyType: values.companyType,
            revenuesRange: values.revenuesRange,
            size: values.size,
            country: values.country !== 'Selecionar' ? values.country : '',
            multinational: values.country !== 'Selecionar' ? values.multinational : false,
            taxId: values.taxId,
            revenuesRangeCurrency: 'USD'
        };

        return putCompanyDetails(companyPayload)
            .then(() => {
                updateSteps({ step2: true });
                if (companyDetails.hasPocs === 0) {
                    RHP.push('/ranking/latam/corporate/editPocs');
                } else {
                    RHP.push('/ranking/latam/corporate/step3');
                }
            })
            .catch(() => form.submited());
    };

    const evSubmit = ev => form.handleSubmit(ev, onSubmit);

    const minDatePocs = new Date('2025-02-01');

    const isOutdatedPoc =
        !companyPocs.lastRequest ||
        !companyPocs.lastRequest.regDate ||
        new Date(companyPocs.lastRequest.regDate) < minDatePocs;

    return (
        <Page.Page
            name="CorporateStep2"
            back="/ranking/latam/corporate"
            customBackText={t('glossary:backToRanking')}
        >
            <Prompt
                when={formDataChanged}
                message="Você tem alterações não salvas. Tem certeza de que deseja sair? jsx"
            />
            <Page.Title>{t('CorporateStep2.title')}</Page.Title>
            <Page.Descr>{t('CorporateStep2.descr', { rankingYear: ranking.year })}</Page.Descr>

            <Form.Form name="CorporateStep2Form" onSubmit={evSubmit}>
                <Form.Field {...fields.name} />

                {/* <Styled.LogoUploadContainer>
                    <Form.Field {...fields.logourl} style={{ flex: 0 }} />
                    <span>{t('CorporateStep2.uploadImage')}</span>
                </Styled.LogoUploadContainer> */}

                {/* <Form.Field {...fields.domains} />
                <Page.Descr style={{ fontSize: 'small', opacity: 0.7 , marginTop: '0.5rem' }}>
                    {t('CorporateStep2.associatedDomainsDescription')}
                </Page.Descr> */}

                {companyDetails?.domains?.length && (
                    <Styled.AssociatedDomain>
                        <h3>{t('CorporateStep2.associatedDomainsTitle')}</h3>
                        <div>
                            {companyDetails.domains.map(domain => (
                                <p key={domain}>{domain}</p>
                            ))}
                        </div>
                        <span
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: t('CorporateStep2.associatedDomainsDescription')
                            }}
                        />
                    </Styled.AssociatedDomain>
                )}

                <Form.Field {...fields.awardName} />
                <Form.Field {...fields.companySector} />
                <Form.Field {...fields.companyType} />
                <Form.Field {...fields.country} />
                <Form.Field {...fields.taxId} />
                <Form.Field {...fields.size} />
                <Form.Field {...fields.revenuesRange} />
                <br />
                {groupName && (
                    <p style={{ fontWeight: 'bold' }}>
                        {t('ranking:CorporateStep2.groupName')}
                        {groupName}
                    </p>
                )}

                {fields.country?.value && fields.country?.value !== 'Selecionar' && (
                    <Form.Field {...fields.multinational} />
                )}

                {corpDontMeetRequirements && (
                    <Alert.AlertRow {...propsCorpDoesntMeetRequirements} />
                )}

                {isMiddleMarket && (
                    <div>
                        <Alert.AlertRow {...propsCorpIsMiddleMarket} />
                        <Form.Field {...fields.isMiddleMarket} />
                    </div>
                )}

                {notDeclaredRevenues && <Alert.AlertRow {...propsNotDeclaredRevenues} />}

                {companyDetails.hasPocs > 0 && (
                    <div>
                        <Page.Descr style={{ fontWeight: 'bold' }}>
                            {t('CorporateStep2.pocTitle')}
                        </Page.Descr>
                        {companyPocs.pocs
                            .filter(e => e.pocs.includes('operational'))
                            .map(poc => (
                                <PocsCard
                                    key={poc.email}
                                    data={poc}
                                    operationalOnly
                                    useRankingCorporate={useRankingCorporate}
                                />
                            ))}
                        {isOutdatedPoc ? (
                            <Alert.AlertRow style={{ marginTop: 15 }} {...propsOutdatedPocs} />
                        ) : (
                            <p
                                style={{ marginTop: 15 }}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: t('CorporateStep2.editPocs')
                                }}
                            />
                        )}
                    </div>
                )}

                {companyDetails.hasPocs === 0 && (
                    <Page.Descr>{t('CorporateStep2.addPocs')}</Page.Descr>
                )}

                <div style={{ display: 'flex', gap: '10px' }}>
                    <Page.Buttn style={{ width: '25%' }} to="/ranking/latam/corporate/step1">
                        <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                        <span className="text" style={{ margin: '0 0 0 auto' }}>
                            {t('glossary:back')}
                        </span>
                    </Page.Buttn>
                    <div style={{ width: '80%' }}>
                        <Form.Submt paddingTop="10rem" {...form} text={t('glossary:save')} />
                    </div>
                </div>
            </Form.Form>
        </Page.Page>
    );
}
